import { createStore } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { isLocal } from 'utils/env.utils';

import { LoyaltyTierName } from 'types/api/account.types';

export type NofomoLoyaltyStoreState = {
  cartId: string;
  previous: {
    points: number;
    tier: LoyaltyTierName;
  };
  hasPlayed: boolean;
};

type NofomoLoyaltyStoreActions = {
  setLoyaltyCelebration: (
    cartId: NofomoLoyaltyStoreState['cartId'],
    previous: NofomoLoyaltyStoreState['previous'],
  ) => void;
  setHasPlayed: () => void;
};

export type NofomoLoyaltyStore = NofomoLoyaltyStoreState & {
  actions: NofomoLoyaltyStoreActions;
};

export const initNofomoLoyaltyStore = ({
  ...state
}: Partial<NofomoLoyaltyStoreState>): NofomoLoyaltyStoreState => ({
  cartId: '',
  previous: {
    points: 0,
    tier: 'Bronze',
  },
  hasPlayed: false,
  ...state,
});

export type NofomoLoyaltyStoreReturnType = ReturnType<
  typeof createLoyaltyStore
>;

type SessiontorageState = Pick<
  NofomoLoyaltyStoreState,
  'cartId' | 'previous' | 'hasPlayed'
>;

export const createLoyaltyStore = (initState: NofomoLoyaltyStoreState) => {
  const store = createStore<NofomoLoyaltyStore>()(
    devtools(
      immer(
        persist(
          (set) => ({
            ...initState,
            actions: {
              setLoyaltyCelebration: (cartId, previous) => {
                set(
                  (state) => {
                    state.cartId = cartId;
                    state.previous = previous;
                    state.hasPlayed = false;
                  },
                  false,
                  { type: 'setLoyaltyCelebration' },
                );
              },
              setHasPlayed: () => {
                set(
                  (state) => {
                    state.hasPlayed = true;
                    state.cartId = '';
                  },
                  false,
                  { type: 'setHasPlayed' },
                );
              },
            },
          }),

          {
            // Funny name for nofomo-store
            name: `nf-loyalty`,
            partialize: (state): SessiontorageState => ({
              hasPlayed: state.hasPlayed,
              cartId: state.cartId,
              previous: state.previous,
            }),
            storage: createJSONStorage(() => sessionStorage),
          },
        ),
      ),
      { name: 'nofomo-loyalty', store: 'nf-loyalty', enabled: isLocal },
    ),
  );

  return store;
};
